import React, { memo, useState } from "react";
import { useGoogleMap } from "@/GoogleMapContext";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
} from "@mui/material";
import { GoogleMap, InfoWindow, Marker } from "@react-google-maps/api";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnIcon from "@mui/icons-material/LocationOn";

interface ViewLocationProps {
  modalOpen: boolean;
  closeModal: () => void;
  title: string;
  lat: string;
  lng: string;
  width?: string;
}

const mapContainerStyle = {
  width: "100%",
  height: "50vh",
  borderRadius: "8px",
};

const mapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  streetViewControl: true,
  mapTypeControl: true,
  fullscreenControl: true,
};

const ViewLocation: React.FC<ViewLocationProps> = ({
  modalOpen,
  closeModal,
  title,
  lat,
  lng,
  width,
}) => {
  const [address, setAddress] = useState<string>("");
  const [showInfo, setShowInfo] = useState<boolean>(false);

  const { isLoaded, loadError } = useGoogleMap();

  if (loadError) return null;
  if (!modalOpen || !lat || !lng) return null;

  const parsedLat = Number(lat);
  const parsedLng = Number(lng);

  if (!Number.isFinite(parsedLat) || !Number.isFinite(parsedLng)) {
    return null;
  }

  const mapCenter = { lat: parsedLat, lng: parsedLng };

  const getAddress = () => {
    if (typeof window === "undefined" || !window.google?.maps) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: mapCenter }, (results, status) => {
      if (status === "OK" && results?.[0]) {
        setAddress(results[0].formatted_address);
        setShowInfo(true);
      }
    });
  };

  const hideGoogleCloseButton = () => {
    const closeButton = document.querySelector(
      ".gm-ui-hover-effect"
    ) as HTMLElement;
    if (closeButton) closeButton.style.display = "none";
  };

  const handleClose = () => {
    setShowInfo(false);
    setAddress("");
    closeModal();
  };

  return (
    <Dialog
      open={modalOpen}
      onClose={handleClose}
      fullWidth
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            width: width || "650px",
            maxWidth: "94vw",
            borderRadius: "14px",
            overflow: "hidden",
            border: "1px solid #e9edf5",
            boxShadow: "0 10px 30px rgba(38, 46, 77, 0.12)",
          },
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          px: 2.5,
          py: 1.75,
          borderBottom: "1px solid #edf0f7",
          background: "linear-gradient(180deg, #fafbfe 0%, #ffffff 100%)",
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-[#eef2ff] text-[#5d78ff] flex items-center justify-center">
              <LocationOnIcon sx={{ fontSize: 17 }} />
            </div>
            <Typography
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontWeight: 600,
                fontSize: "14.5px",
                color: "#1e2433",
                lineHeight: 1.25,
              }}
            >
              {title || "View Location"}
            </Typography>
          </div>

          <IconButton
            size="small"
            onClick={handleClose}
            sx={{
              color: "#94a3b8",
              "&:hover": { color: "#1e2433", backgroundColor: "#f1f5f9" },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </div>
      </DialogTitle>

      {/* Map Content */}
      <DialogContent sx={{ p: 2, backgroundColor: "#ffffff" }}>
        <Box
          sx={{
            borderRadius: "8px",
            overflow: "hidden",
            border: "1px solid #edf0f7",
          }}
        >
          {isLoaded ? (
            <GoogleMap
              center={mapCenter}
              zoom={15}
              mapContainerStyle={mapContainerStyle}
              options={mapOptions}
            >
              <Marker position={mapCenter} onClick={getAddress} />

              {showInfo && address && (
                <InfoWindow
                  position={mapCenter}
                  onDomReady={hideGoogleCloseButton}
                  options={{
                    maxWidth: 220,
                    pixelOffset: new window.google.maps.Size(0, -40),
                    disableAutoPan: true,
                  }}
                >
                  <div
                    style={{
                      fontFamily: "Poppins, sans-serif",
                      padding: "3px 2px",
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: "8px",
                    }}
                  >
                    <div style={{ fontSize: "11.5px", color: "#1e2433", lineHeight: 1.35 }}>
                      <strong style={{ color: "#5d78ff" }}>Address: </strong>
                      {address}
                    </div>
                    <button
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "#94a3b8",
                        fontSize: "13px",
                        padding: 0,
                      }}
                      onClick={() => setShowInfo(false)}
                    >
                      ×
                    </button>
                  </div>
                </InfoWindow>
              )}
            </GoogleMap>
          ) : (
            <Box
              sx={{
                height: "50vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#f1f5f9",
                color: "#64748b",
                fontFamily: "Poppins, sans-serif",
              }}
            >
              Loading map...
            </Box>
          )}
        </Box>
      </DialogContent>

      {/* Footer */}
      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          borderTop: "1px solid #edf0f7",
          backgroundColor: "#fafbfe",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          onClick={handleClose}
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontSize: "13px",
            fontWeight: 500,
            textTransform: "none",
            backgroundColor: "#5d78ff",
            color: "#ffffff",
            borderRadius: "8px",
            px: 2.5,
            height: "34px",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#4863e6",
              boxShadow: "none",
            },
          }}
          variant="contained"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(ViewLocation);
